import React, { useState, useCallback, useEffect } from 'react';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Folder,
  FolderOpen,
  Loader2,
  DatabaseZap,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { erpKnowledgeBaseService } from '@/services/erpKnowledgeBaseService';

import { logger } from '@/utils/logger';
interface FolderData {
  id: string;
  name: string;
  description?: string;
  children_count: number;
  parent_id?: string;
}

interface AddToKnowledgeBaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onConfirm: (folderId: string, folderName: string) => void;
  // File names of selected cloud-source (Google Drive / SharePoint) docs.
  // These cannot be added to the Knowledge Base and are skipped; the dialog
  // surfaces them so the operator knows exactly what won't be linked.
  skippedCloudFiles?: string[];
}

const PAGE_SIZE = 20;

interface PaginationInfo {
  has_next: boolean;
  page: number;
  total_count: number;
}

/**
 * Fetch the full breadcrumb path for a Knowledge Base item using the server API.
 * Returns an array of document_name strings from root to the item.
 */
async function fetchBreadcrumb(folderId: string): Promise<string[]> {
  try {
    const response = await axios.post(
      getApiEndpoint('GET_BREADCRUMB'),
      { name: folderId },
      { withCredentials: true }
    );
    const data = response.data?.message;
    if (data?.success && data.data?.length > 0) {
      return data.data.map((item: any) => item.document_name);
    }
  } catch (error) {
    logger.error('[AddToKB] Failed to fetch breadcrumb:', error);
  }
  return [];
}

const AddToKnowledgeBaseDialog: React.FC<AddToKnowledgeBaseDialogProps> = ({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
  skippedCloudFiles = [],
}) => {
  // Documents that will actually be linked once cloud-source files are
  // skipped. Used for the header count and to disable confirm when nothing
  // is left to add.
  const linkableCount = Math.max(0, selectedCount - skippedCloudFiles.length);

  // Skipped-files banner: keep it compact no matter how many are skipped.
  // Show a short preview by default; reveal the full (capped, scrollable)
  // list on demand so 100 skipped files don't blow out the dialog height.
  const SKIPPED_PREVIEW = 5;
  const [showAllSkipped, setShowAllSkipped] = useState(false);
  const visibleSkipped = showAllSkipped
    ? skippedCloudFiles
    : skippedCloudFiles.slice(0, SKIPPED_PREVIEW);
  // Collapse the expanded list again whenever the dialog reopens.
  useEffect(() => {
    if (!open) setShowAllSkipped(false);
  }, [open]);

  const [foldersByParent, setFoldersByParent] = useState<Map<string, FolderData[]>>(new Map());
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState<{ id: string; name: string } | null>(null);

  // Pagination state per parent folder
  const [paginationByParent, setPaginationByParent] = useState<Map<string, PaginationInfo>>(new Map());
  const [loadingMoreFolders, setLoadingMoreFolders] = useState<Set<string>>(new Set());

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FolderData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPagination, setSearchPagination] = useState<PaginationInfo | null>(null);
  const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Full breadcrumb path for the selected folder (loaded from API)
  const [breadcrumbPath, setBreadcrumbPath] = useState<string[]>([]);

  const fetchFolders = useCallback(async (parentId: string = '', page: number = 1) => {
    try {
      if (page === 1 && parentId) {
        setLoadingFolders((prev) => new Set(prev).add(parentId));
      }
      if (page > 1) {
        setLoadingMoreFolders((prev) => new Set(prev).add(parentId));
      }

      const result = await erpKnowledgeBaseService.getFolderContents(
        parentId || undefined,
        true,
        page,
        PAGE_SIZE
      );

      const foldersData: FolderData[] = (result.data || [])
        .filter((item: any) => item.is_folder === 1)
        .map((item: any) => ({
          id: item.name,
          name: item.document_name || item.name,
          description: item.description,
          children_count: item.folders_count || 0,
          parent_id: parentId,
        }));

      setFoldersByParent((prev) => {
        const next = new Map(prev);
        if (page > 1) {
          const existing = next.get(parentId) || [];
          next.set(parentId, [...existing, ...foldersData]);
        } else {
          next.set(parentId, foldersData);
        }
        return next;
      });

      // Store pagination info
      if (result.pagination) {
        setPaginationByParent((prev) => {
          const next = new Map(prev);
          next.set(parentId, {
            has_next: result.pagination!.has_next,
            page: result.pagination!.page,
            total_count: result.pagination!.total_count,
          });
          return next;
        });
      }
    } catch (error) {
      logger.error('Error fetching folders:', error);
      toast.error('Failed to load folders');
    } finally {
      if (parentId) {
        setLoadingFolders((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
      setLoadingMoreFolders((prev) => {
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
      setIsInitialLoading(false);
    }
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setSelectedFolder(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!query.trim()) {
      setSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await erpKnowledgeBaseService.searchItems(query.trim(), undefined, true, 1, PAGE_SIZE);
        const folders: FolderData[] = (result.data || [])
          .filter((item: any) => item.is_folder === 1)
          .map((item: any) => ({
            id: item.name,
            name: item.document_name || item.name,
            description: item.description,
            children_count: item.folders_count || 0,
            parent_id: item.parent_directory || '',
          }));
        setSearchResults(folders);
        setSearchPagination(result.pagination ? {
          has_next: result.pagination.has_next,
          page: result.pagination.page,
          total_count: result.pagination.total_count,
        } : null);
      } catch (error) {
        logger.error('Folder search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  const loadMoreSearchResults = useCallback(async () => {
    if (!searchPagination?.has_next || isLoadingMoreSearch) return;
    const nextPage = searchPagination.page + 1;
    setIsLoadingMoreSearch(true);
    try {
      const result = await erpKnowledgeBaseService.searchItems(searchQuery.trim(), undefined, true, nextPage, PAGE_SIZE);
      const folders: FolderData[] = (result.data || [])
        .filter((item: any) => item.is_folder === 1)
        .map((item: any) => ({
          id: item.name,
          name: item.document_name || item.name,
          description: item.description,
          children_count: item.folders_count || 0,
          parent_id: item.parent_directory || '',
        }));
      setSearchResults((prev) => [...prev, ...folders]);
      setSearchPagination(result.pagination ? {
        has_next: result.pagination.has_next,
        page: result.pagination.page,
        total_count: result.pagination.total_count,
      } : null);
    } catch (error) {
      logger.error('Load more search failed:', error);
    } finally {
      setIsLoadingMoreSearch(false);
    }
  }, [searchPagination, isLoadingMoreSearch, searchQuery]);

  const loadMoreFolders = useCallback(async (parentId: string) => {
    const pagination = paginationByParent.get(parentId);
    if (!pagination?.has_next || loadingMoreFolders.has(parentId)) return;
    await fetchFolders(parentId, pagination.page + 1);
  }, [paginationByParent, loadingMoreFolders, fetchFolders]);

  useEffect(() => {
    if (open) {
      setSelectedFolder(null);
      setExpandedFolders(new Set());
      setFoldersByParent(new Map());
      setPaginationByParent(new Map());
      setSearchQuery('');
      setSearchResults([]);
      setSearchPagination(null);
      setBreadcrumbPath([]);
      setIsInitialLoading(true);
      fetchFolders('');
    }
  }, [open, fetchFolders]);

  const toggleFolder = useCallback(
    async (folderId: string) => {
      const isExpanded = expandedFolders.has(folderId);

      if (isExpanded) {
        setExpandedFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      } else {
        if (!foldersByParent.has(folderId)) {
          await fetchFolders(folderId);
        }
        setExpandedFolders((prev) => new Set(prev).add(folderId));
      }
    },
    [expandedFolders, foldersByParent, fetchFolders]
  );

  // Build a flat lookup of all loaded folders by ID (including search results and ancestors)
  const allFoldersById = new Map<string, FolderData>();
  foldersByParent.forEach((folders) => {
    folders.forEach((f) => allFoldersById.set(f.id, f));
  });
  searchResults.forEach((f) => allFoldersById.set(f.id, f));

  // Build full breadcrumb path for a folder
  const getFolderPath = (folderId: string): string[] => {
    const path: string[] = [];
    let current = allFoldersById.get(folderId);
    while (current) {
      path.unshift(current.name);
      current = current.parent_id ? allFoldersById.get(current.parent_id) : undefined;
    }
    return path;
  };

  const handleSelectFolder = async (folder: FolderData) => {
    if (selectedFolder?.id === folder.id) {
      setSelectedFolder(null);
      setBreadcrumbPath([]);
    } else {
      setSelectedFolder({ id: folder.id, name: folder.name });
      // Show local path immediately, then fetch full path from API
      setBreadcrumbPath(getFolderPath(folder.id));
      const fullPath = await fetchBreadcrumb(folder.id);
      if (fullPath.length > 0) {
        setBreadcrumbPath(fullPath);
      }
    }
  };

  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!selectedFolder) return;
    setConfirming(true);
    try {
      // Fetch full breadcrumb path from server API
      const fullPath = await fetchBreadcrumb(selectedFolder.id);
      const pathStr = fullPath.length > 0
        ? fullPath.join(' > ')
        : (breadcrumbPath.length > 0 ? breadcrumbPath.join(' > ') : selectedFolder.name);
      onConfirm(selectedFolder.id, pathStr);
    } finally {
      setConfirming(false);
    }
  };

  const renderFolderItem = (folder: FolderData, level: number) => {
    const isExpanded = expandedFolders.has(folder.id);
    const isLoading = loadingFolders.has(folder.id);
    const isSelected = selectedFolder?.id === folder.id;
    const hasChildren = folder.children_count > 0;
    const paddingLeft = level * 24 + 12;

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center py-2.5 px-3 rounded-lg cursor-pointer transition-colors ${
            isSelected
              ? 'bg-primary/10 border border-primary/30'
              : 'hover:bg-muted/50 border border-transparent'
          }`}
          style={{ marginLeft: `${paddingLeft}px` }}
          onClick={() => handleSelectFolder(folder)}
        >
          {/* Expand/Collapse */}
          <div
            className="w-5 h-5 flex items-center justify-center mr-1 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggleFolder(folder.id);
            }}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : hasChildren ? (
              isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )
            ) : (
              <span className="w-4" />
            )}
          </div>

          {/* Folder Icon */}
          {isExpanded && hasChildren ? (
            <FolderOpen className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
          ) : (
            <Folder className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
          )}

          {/* Folder Name */}
          <span className="text-sm truncate flex-1">{folder.name}</span>
        </div>

        {/* Children */}
        {isExpanded && renderFolders(folder.id, level + 1)}
      </div>
    );
  };

  const renderFolders = (parentId: string, level: number): React.ReactNode => {
    const folders = foldersByParent.get(parentId) || [];
    const pagination = paginationByParent.get(parentId);
    const isLoadingMore = loadingMoreFolders.has(parentId);
    const paddingLeft = level * 24 + 12;

    return (
      <>
        {folders.map((folder) => renderFolderItem(folder, level))}
        {pagination?.has_next && (
          <div style={{ marginLeft: `${paddingLeft}px` }} className="py-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground h-7 px-3"
              disabled={isLoadingMore}
              onClick={() => loadMoreFolders(parentId)}
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Loading...
                </>
              ) : (
                'Load more...'
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  const rootFolders = foldersByParent.get('') || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DatabaseZap className="h-5 w-5" />
            Add to Knowledge Base
          </DialogTitle>
          <DialogDescription>
            Select a folder for {linkableCount} document{linkableCount !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Cloud-source files cannot be added to the KB - list what will be
            skipped so the operator sees exactly what's missing. */}
        {skippedCloudFiles.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm">
            <div className="flex items-center gap-2 font-medium text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {skippedCloudFiles.length} cloud-source file{skippedCloudFiles.length !== 1 ? 's' : ''} will be skipped
            </div>
            <p className="mt-1 text-amber-700">
              Files synced from Google Drive / SharePoint can't be added to the Knowledge Base:
            </p>
            <ul
              className={`mt-1 list-disc pl-5 text-amber-700 ${
                showAllSkipped ? 'max-h-32 overflow-y-auto' : ''
              }`}
            >
              {visibleSkipped.map((name, i) => (
                // break-all (not truncate): long no-space filenames must WRAP
                // onto multiple lines, not force a horizontal scrollbar on the
                // dialog. Vertical scroll (max-h-32 above) handles long lists.
                <li key={`${name}-${i}`} className="break-all">{name}</li>
              ))}
            </ul>
            {skippedCloudFiles.length > SKIPPED_PREVIEW && (
              <button
                type="button"
                onClick={() => setShowAllSkipped(v => !v)}
                className="mt-1.5 text-xs font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
              >
                {showAllSkipped
                  ? 'Show less'
                  : `Show all ${skippedCloudFiles.length}`}
              </button>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search folders..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Folder Tree / Search Results */}
        <div className="border rounded-lg min-h-[300px] max-h-[400px] overflow-y-auto py-2">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading folders...</span>
            </div>
          ) : searchQuery.trim() ? (
            isSearching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Folder className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No folders match "{searchQuery}"</p>
              </div>
            ) : (
              <>
                {searchResults.map((folder) => {
                  const isSelected = selectedFolder?.id === folder.id;
                  return (
                    <div
                      key={folder.id}
                      className={`flex items-center py-2.5 px-4 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary/10 border-l-2 border-primary'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleSelectFolder(folder)}
                    >
                      <Folder className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                      <span className="text-sm truncate">{folder.name}</span>
                    </div>
                  );
                })}
                {searchPagination?.has_next && (
                  <div className="py-1 px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground h-7 px-3"
                      disabled={isLoadingMoreSearch}
                      onClick={loadMoreSearchResults}
                    >
                      {isLoadingMoreSearch ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          Loading...
                        </>
                      ) : (
                        'Load more...'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )
          ) : rootFolders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Folder className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No folders found</p>
            </div>
          ) : (
            renderFolders('', 0)
          )}
        </div>

        {/* Selected folder breadcrumb - green alert */}
        {selectedFolder && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
            <div className="text-sm text-emerald-300 flex items-center gap-1 flex-wrap">
              <span className="font-medium">Selected:</span>
              {(breadcrumbPath.length > 0 ? breadcrumbPath : [selectedFolder.name]).map((segment, idx, arr) => (
                <span key={idx} className="flex items-center gap-1">
                  <span className={idx === arr.length - 1 ? 'font-semibold' : ''}>
                    {segment}
                  </span>
                  {idx < arr.length - 1 && (
                    <ChevronRight className="h-3 w-3 text-emerald-400" />
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedFolder || confirming || linkableCount === 0}>
            {confirming ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <DatabaseZap className="h-4 w-4 mr-2" />
            )}
            {linkableCount === 0
              ? 'Nothing to add'
              : `Add ${linkableCount} to Knowledge Base`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToKnowledgeBaseDialog;
